import { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, X, Globe, ChevronDown, BookOpen } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { blogListPath } from "@/lib/blog";

const langLabels = { tr: "Türkçe", en: "English", ar: "العربية" };
const langFlags = { tr: "🇹🇷", en: "🇬🇧", ar: "🇸🇦" };

export function Navbar() {
  const { t, lang, setLang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Mevcut dilin ana sayfa path'i
  const langHomeMap = { tr: "/", en: "/en", ar: "/ar" };
  const homePath = langHomeMap[lang] || "/";
  const isOnHome = location.pathname === homePath;

  const navLinks = [
    { label: t("nav.features"), href: "#features" },
    { label: t("nav.pricing"), href: "#pricing" },
    { label: t("nav.faq"), href: "#faq" },
    { label: t("nav.contact"), href: "#contact" },
  ];

  const scrollTo = (href) => {
    setMobileOpen(false);
    if (isOnHome) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(homePath);
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl shadow-sm border-b border-slate-100"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <a href={homePath} className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <img
              src="/images/entegrelogo/EP-Logo-Normal.svg"
              alt={t("imageAlt.logo")}
              width="160"
              height="40"
              className="h-8 sm:h-10"
              fetchpriority="high"
            />
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href, link.route)}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                data-testid={`nav-${link.href.slice(1)}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Right Actions */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Blog link — sağda, dil switcher'dan önce */}
            <Link
              to={blogListPath(lang)}
              onClick={() => setMobileOpen(false)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-50 transition-colors"
              data-testid="nav-blog"
            >
              <BookOpen className="w-4 h-4" />
              {t("nav.blog")}
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-50 transition-colors"
                data-testid="language-switcher"
              >
                <Globe className="w-4 h-4" />
                <span>{langLabels[lang]}</span>
                <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {Object.entries(langLabels).map(([code, label]) => (
                  <DropdownMenuItem
                    key={code}
                    onClick={() => setLang(code)}
                    className={`cursor-pointer ${lang === code ? "bg-sky-50 text-sky-700" : ""}`}
                    data-testid={`lang-${code}`}
                  >
                    <span className="mr-2">{langFlags[code]}</span>
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <a
              href="https://bayi.entegrepanel.net/login"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 rounded-lg hover:bg-slate-50 transition-colors"
              data-testid="demo-login-btn"
            >
              {t("nav.demoLogin")}
            </a>
            <a
              href="https://bayi.entegrepanel.net/register"
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2.5 text-sm font-semibold text-white rounded-full bg-[#22A7D1] hover:bg-[#1B8FB5] transition-all hover:-translate-y-0.5 hover:shadow-lg"
              data-testid="register-btn"
            >
              {t("nav.register")}
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            className="lg:hidden p-2 text-slate-600"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 shadow-xl" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href)}
                className="block w-full text-left px-4 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
              >
                {link.label}
              </button>
            ))}
            {/* Mobile Blog */}
            <Link
              to={blogListPath(lang)}
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 w-full px-4 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              {t("nav.blog")}
            </Link>
            <div className="flex gap-2 pt-3 border-t border-slate-100">
              {Object.entries(langLabels).map(([code, label]) => (
                <button
                  key={code}
                  onClick={() => { setLang(code); setMobileOpen(false); }}
                  className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                    lang === code ? "bg-sky-50 text-sky-700 border border-sky-200" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {langFlags[code]} {label}
                </button>
              ))}
            </div>
            <div className="flex gap-2 pt-3">
              <a
                href="https://bayi.entegrepanel.net/login"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-center py-2.5 text-sm font-medium border border-slate-200 rounded-full text-slate-700 hover:bg-slate-50 transition-colors"
              >
                {t("nav.demoLogin")}
              </a>
              <a
                href="https://bayi.entegrepanel.net/register"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-center py-2.5 text-sm font-semibold text-white rounded-full bg-[#22A7D1] hover:bg-[#1B8FB5] transition-colors"
              >
                {t("nav.register")}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
